import React, { useEffect, useRef, useState } from 'react';

type Shaders = Pick<typeof import('@paper-design/shaders-react'), 'MeshGradient' | 'PulsingBorder'>;
const colors = ['#06172f', '#09213e', '#185eac', '#317cff', '#38c7ed', '#11275c'];

class ShaderBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? null : this.props.children; }
}

const HeroBackdrop = ({ children }: { children: (signature: React.ReactNode) => React.ReactNode }) => {
  const surface = useRef<HTMLDivElement>(null);
  const [shaders, setShaders] = useState<Shaders | null>(null);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const [introVisible, setIntroVisible] = useState(true);
  useEffect(() => {
    let active = true;
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const preference = () => setReduced(Boolean(media?.matches));
    const visibility = () => setPageVisible(!document.hidden);
    preference(); visibility();
    media?.addEventListener?.('change', preference);
    document.addEventListener('visibilitychange', visibility);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    if (surface.current) observer?.observe(surface.current);
    const homepage = surface.current?.closest('.orarivo-home');
    const intro = new MutationObserver(() => setIntroVisible(Boolean(homepage?.querySelector('.orarivo-splash'))));
    setIntroVisible(Boolean(homepage?.querySelector('.orarivo-splash')));
    if (homepage) intro.observe(homepage, { childList: true });
    // Load the reference's shaders only in a capable browser; public HTML stays complete.
    if ('WebGL2RenderingContext' in window) {
      try {
        const probe = document.createElement('canvas').getContext('webgl2');
        if (probe) {
          probe.getExtension('WEBGL_lose_context')?.loseContext();
          import('@paper-design/shaders-react').then(({ MeshGradient, PulsingBorder }) => {
            if (active) setShaders({ MeshGradient, PulsingBorder });
          }).catch(() => {});
        }
      } catch { /* Keep the CSS background when graphics are unavailable. */ }
    }
    return () => {
      active = false;
      observer?.disconnect();
      intro.disconnect();
      media?.removeEventListener?.('change', preference);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);
  const moving = inView && pageVisible && !reduced && !introVisible;
  const Mesh = shaders?.MeshGradient;
  const Pulse = shaders?.PulsingBorder;
  const signature = <div className="orarivo-shader-signature" aria-hidden="true">
      {Pulse && <ShaderBoundary><Pulse className="orarivo-signature-effect" smoke={0} smokeSize={0} colors={['#185eac', '#317cff', '#38c7ed', '#e1f5ee']} colorBack="#00000000" speed={moving ? .75 : 0} frame={42000} roundness={1} thickness={.07} softness={.35} intensity={1.3} pulse={.16} scale={.85} minPixelRatio={1} maxPixelCount={40000} style={{ width: '100%', height: '100%' }}/></ShaderBoundary>}
      <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt=""/>
    </div>;
  return <div className="orarivo-opening" ref={surface}>
    <div className="orarivo-backdrop-track" aria-hidden="true"><div className="orarivo-shader-backdrop">
      {Mesh && <ShaderBoundary><Mesh className="orarivo-shader-mesh" colors={colors} distortion={.75} swirl={.65} grainMixer={0} grainOverlay={0} speed={moving ? .18 : 0} frame={42000} scale={1.1} offsetX={-.18} minPixelRatio={1} maxPixelCount={1400000} style={{ width: '100%', height: '100%' }}/></ShaderBoundary>}
      <div className="orarivo-shader-shade"/>
    </div></div>
    {children(signature)}
  </div>;
};
export default HeroBackdrop;
